// components/daimon/DaimonTour.tsx
'use client';

// THE DAIMON'S QUICK TOUR (owner, 2026-10-05: the Daimon opens to every user, 'add an onboarding tour
// that explains how to use it'). Opt-in from the Daimon home, the Slides tour's method: a dimmed page,
// a short pulse on the real control, a card beside it. The guide only explains: it never starts a
// call, sends a message, opens a picker, shares a screen or changes the mode.
import React, { useEffect, useRef, useState } from 'react';
import { create } from 'zustand';
import { useKernel } from '@/kernel/Kernel';
import { useKernelStore } from '@/kernel/kernelStores';
import { useNoticeDialog } from '@/components/portal/TrialNotice';
import { canAccessDaimon } from '#lib/access/daimonAccess';
import { visibleBox, type TourBox } from '@/components/ui/tourGeometry';
import styles from '@/components/ui/GuidedTour.module.css';

type TourStep = { target: string | null; title: string; text: string; hint?: string };

export const daimonTourSteps = ({ summon, share }: { summon: string; share: string }): TourStep[] => [
  { target: 'orb', title: 'Talk to the Daimon', text: `Tap her orb to start a voice conversation, or press ${summon} anywhere in Fater. Speak naturally and interrupt her whenever you like. Voice conversations use your organisation's credits.` },
  { target: 'input', title: 'Or type to her', text: 'Write a request here and press Enter. The microphone beside it dictates into the box; the blue button starts a voice conversation, like the orb.' },
  { target: 'plus', title: 'Show her something', text: `The + button adds a photo, or shares your screen so she sees what you see (${share}). The guide opens neither.` },
  { target: 'mode', title: 'Choose how free she is', text: 'Manual: she asks before every change and tells you the price of anything paid. Accept edits: she makes changes and spends without asking, but deleting or stopping waits for you. Auto: she also decides alone on what you plainly asked, deletions included.' },
  { target: 'recents', title: 'Find your conversations', text: 'Every conversation is kept in Recents in the left bar. Fater opens on a fresh one; click a conversation to pick it up where you left it.', hint: 'Your conversations appear here after the first one.' },
  { target: null, title: 'She works in Fater with you', text: 'Ask her to open a campaign, a brand, a deck or a project: she navigates and clicks for you, and you can take over at any moment by moving your mouse. When you leave this page she stays with you as a small orb in the corner.' },
];

/** Transient: who is touring and where. Bound to the user so an account switch never inherits it. */
export const useDaimonTour = create<{ tour: { userId: string; step: number } | null }>(() => ({ tour: null }));

export const DaimonTourButton = () => {
  const { kernel } = useKernel();
  const user = useKernelStore((state) => state.userContext);
  if (!user.dbUserId || !canAccessDaimon(user)) return null;
  return (
    <button type="button" data-daimon-tour-start className="text-[#AFAFAF] underline decoration-[#444] underline-offset-4 transition-colors hover:text-[#ECECEC] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white"
      style={{ fontSize: 14 }}
      onClick={() => {
        const current = kernel.current?.getUserContext();
        if (!current?.dbUserId || !canAccessDaimon(current)) return;
        useDaimonTour.setState({ tour: { userId: current.dbUserId, step: 0 } });
        kernel.current?.logUserAction('Daimon Tour Started');
      }}>
      Quick tour
    </button>
  );
};

const TourDialog = ({ steps }: { steps: TourStep[] }) => {
  const { kernel } = useKernel();
  const { dialogRef, headingRef, headingId, onKeyDown } = useNoticeDialog();
  const cardRef = useRef<HTMLDivElement>(null);
  const tour = useDaimonTour((state) => state.tour);
  const index = Math.max(0, Math.min(tour?.step ?? 0, steps.length - 1));
  const step = steps[index]!;
  const [geometry, setGeometry] = useState<{ target: TourBox | null; left: number; top: number }>({ target: null, left: 16, top: 16 });

  const stop = () => {
    useDaimonTour.setState({ tour: null });
    kernel.current?.logUserAction('Daimon Tour Closed', { step: index + 1 });
  };
  const move = (delta: number) => {
    const current = useDaimonTour.getState().tour;
    if (current) useDaimonTour.setState({ tour: { ...current, step: Math.max(0, Math.min(steps.length - 1, current.step + delta)) } });
  };

  useEffect(() => {
    headingRef.current?.focus();
    let frame = 0;
    const findTarget = () => (step.target ? document.querySelector<HTMLElement>(`[data-daimon-tour="${step.target}"]`) : null);
    const measure = () => {
      const target = findTarget();
      const box = target ? visibleBox(target) : null;
      const width = cardRef.current?.offsetWidth ?? 320;
      const height = cardRef.current?.offsetHeight ?? 240;
      const left = Math.max(16, Math.min(box?.left ?? (innerWidth - width) / 2, innerWidth - width - 16));
      const below = box ? box.top + box.height + 12 : (innerHeight - height) / 2;
      const top = Math.max(16, Math.min(below + height <= innerHeight - 16 ? below : (box?.top ?? innerHeight) - height - 12, innerHeight - height - 16));
      const next = { target: box, left, top };
      setGeometry((previous) => (JSON.stringify(previous) === JSON.stringify(next) ? previous : next));
    };
    const schedule = () => { cancelAnimationFrame(frame); frame = requestAnimationFrame(measure); };
    const resize = new ResizeObserver(schedule);
    if (cardRef.current) resize.observe(cardRef.current);
    const target = findTarget();
    if (target) {
      // No animated scroll for anyone who asked for reduced motion; the guide never needs movement.
      target.scrollIntoView({ block: 'nearest', inline: 'nearest', behavior: 'instant' });
      resize.observe(target);
    }
    window.addEventListener('resize', schedule);
    window.addEventListener('scroll', schedule, true);
    schedule();
    return () => {
      cancelAnimationFrame(frame);
      resize.disconnect();
      window.removeEventListener('resize', schedule);
      window.removeEventListener('scroll', schedule, true);
    };
  }, [index, step.target, headingRef]);

  return (
    <dialog ref={dialogRef} data-daimon-tour-dialog data-daimon-ui aria-labelledby={headingId} className={styles.dialog}
      onKeyDown={onKeyDown} onKeyUp={(event) => event.stopPropagation()}
      onCancel={(event) => { event.preventDefault(); stop(); }}>
      {geometry.target && <div key={index} aria-hidden="true" className={styles.spotlight} style={{ ...geometry.target, borderRadius: 999 }} />}
      <div ref={cardRef} className={styles.card} style={{ left: geometry.left, top: geometry.top }}>
        <p className={styles.counter}>Daimon quick tour · {index + 1} of {steps.length}</p>
        <h2 ref={headingRef} id={headingId} tabIndex={-1} className={styles.heading}>{step.title}</h2>
        <p className={styles.copy}>{step.text}</p>
        {step.target && !geometry.target && <p className={styles.hint}>{step.hint ?? 'This control is not visible here yet. You can continue and come back to it.'}</p>}
        <div className={styles.footer}>
          <button type="button" className={styles.button} onClick={stop}>Skip</button>
          <div className={styles.navigation}>
            {index > 0 && <button type="button" className={styles.button} onClick={() => move(-1)}>Back</button>}
            <button type="button" className={styles.primary} onClick={() => (index === steps.length - 1 ? stop() : move(1))}>{index === steps.length - 1 ? 'Done' : 'Next'}</button>
          </div>
        </div>
      </div>
    </dialog>
  );
};

/** Mounted with the Daimon's page: leaving the page, switching account or losing access ends the tour. */
export const DaimonTour = ({ summon, share }: { summon: string; share: string }) => {
  const user = useKernelStore((state) => state.userContext);
  const tour = useDaimonTour((state) => state.tour);
  const current = !!tour && tour.userId === user.dbUserId && canAccessDaimon(user);
  useEffect(() => {
    if (tour && !current) useDaimonTour.setState({ tour: null });
  }, [tour, current]);
  useEffect(() => () => { useDaimonTour.setState({ tour: null }); }, []);
  return current ? <TourDialog steps={daimonTourSteps({ summon, share })} /> : null;
};
